/**
 * The legend beneath the visibility grid: one sample chip per state with what it
 * means. Mounted only by `VisibilityMatrix`; it takes no props because every
 * label and colour comes from `visibilityChipVocabulary`, the same map
 * `VisibilityMatrixCell` paints from — the legend can therefore never describe a
 * chip the grid does not draw. The three blanking states share one entry, since
 * `kit blank` / `group blank` / `both blank` differ only in scope; the two
 * locked states get one entry each, because they read as opposite outcomes
 * behind the same dashed border and a shared sample would print one label while
 * the text described both. The samples are spans, not buttons: nothing here is
 * clickable.
 */
import { FC } from 'react';
import { Typography, theme } from 'antd';
import FlexBox from '../../atoms/FlexBox';
import type { VisibilityChipState } from '../ViewVisibility.types';
import { visibilityChipVocabulary } from '../visibilityChipVocabulary';

const { Text } = Typography;

const LEGEND_ENTRIES: { state: VisibilityChipState; meaning: string }[] = [
    { state: 'shown', meaning: 'shown in this view' },
    { state: 'hidden', meaning: 'hidden from this view' },
    { state: 'kit', meaning: 'blank on member rows — kit blank, group blank or both blank' },
    { state: 'alwaysShown', meaning: 'locked by the view — always shown, with no write path here' },
    { state: 'neverShown', meaning: 'locked by the object — never shown in any view, with no write path here' },
];

export const VisibilityMatrixLegend: FC = () => {
    const { token } = theme.useToken();
    const vocabulary = visibilityChipVocabulary(token);

    return (
        <FlexBox gap={10} wrap justifyStart noGrow>
            {LEGEND_ENTRIES.map(({ state, meaning }) => {
                const chip = vocabulary[state];
                return (
                    <FlexBox key={state} gap={5} justifyStart noGrow>
                        <span
                            style={{
                                borderStyle: chip.dashed ? 'dashed' : 'solid',
                                borderWidth: token.lineWidth,
                                borderColor: chip.border,
                                backgroundColor: chip.background,
                                borderRadius: token.borderRadiusSM,
                                paddingInline: token.paddingXS,
                            }}
                        >
                            <Text style={{ color: chip.color, fontSize: token.fontSizeSM }}>{chip.label}</Text>
                        </span>
                        <Text type="secondary">{meaning}</Text>
                    </FlexBox>
                );
            })}
        </FlexBox>
    );
};
